/**
 * 分块坐标与键（DESIGN.md §4.3）。
 *
 * 这层是整个引擎的地基：合成器按 tile 分批、历史按 tile 存差分、驱逐按 tile 计粒度、
 * 脏区按 tile 传播。键的设计一旦松散，后面每一处都要各自处理边界情况。
 *
 * 三条约定：
 * - `TILE = 256`。RGBA8 下每块 256 KB，RGBA16F 下 512 KB——这个量级既让驱逐
 *   有意义（不会因为块太小而全是管理开销），又不会让单块大到拖慢增量重绘。
 * - 坐标是**块坐标**（tx/ty），不是像素坐标。凡是接受像素坐标的地方，
 *   函数名里必须体现，否则两种坐标混用是迟早的事。
 * - 键可比较、可作 Map 索引。用字符串而非对象：Map 的键需要值相等语义，
 *   而结构相同的对象在 JS 里并不相等。
 *
 * tx/ty 可以是任意整数（含负数，ADR-0012）——图层内容可以延伸到文档原点
 * 之外，移动工具需要这个能力才能做到"拖出画布左/上边缘再拖回来"完全无损
 * （往右/往下没有这个问题，因为那个方向坐标只会变大）。**文档/合成器自己**
 * 的块坐标不受影响，画布尺寸固定的那个网格仍然从 (0,0) 开始——那部分约束
 * 活在 `composite.ts`/`composite-cache.ts`/`tilesInRect` 里，不是 `tileKey`
 * 该管的事。下游按 `Map`/字符串键处理坐标的地方（`TileStore`、驱逐/暂存盘、
 * LOD 金字塔的 `parentOf`）天然支持负数，已经过审计确认，见 ADR-0012。
 */

/** 分块边长（像素）。DESIGN.md §4.3 定死为 256。 */
export const TILE = 256;

/** 图层 id 的品牌类型。防止把任意字符串当成图层 id 传进来。 */
export type LayerId = string & { readonly __brand: 'LayerId' };

/** 构造图层 id。集中在这里是为了让"哪些字符串是图层 id"有唯一的入口。 */
export function layerId(value: string): LayerId {
  if (value === '') throw new RangeError('图层 id 不能为空字符串。');
  return value as LayerId;
}

/**
 * LOD 级别。0 为全分辨率，每加一级边长减半。
 *
 * DESIGN.md §4.3 要求维护 1/2 到 1/64，即 0–6 共 7 级。
 * 上限不是随意定的：1/64 时 8000² 文档整层只有 125² 像素，再降已无收益。
 */
export type LodLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const MAX_LOD: LodLevel = 6;

/** 分块键。`lod` 省略时为 0（全分辨率）。 */
export interface TileKey {
  readonly layer: LayerId;
  /** 块坐标 x。任意整数——图层内容允许延伸到画布原点之外（ADR-0012）。 */
  readonly tx: number;
  /** 块坐标 y，同上。 */
  readonly ty: number;
  readonly lod: LodLevel;
}

export function tileKey(layer: LayerId, tx: number, ty: number, lod: LodLevel = 0): TileKey {
  if (!Number.isInteger(tx) || !Number.isInteger(ty)) {
    throw new RangeError(`块坐标必须是整数，收到 (${tx}, ${ty})。`);
  }
  return { layer, tx, ty, lod };
}

/**
 * 键的字符串形式，用作 Map 索引。
 *
 * 分隔符用 ` `：图层 id 可能含用户输入的任意可打印字符，
 * 用 `:` 或 `/` 分隔会在图层名里出现同样字符时产生键碰撞——
 * 两个不同的块映射到同一个键，表现为像素莫名其妙地互相覆盖。
 */
export function tileKeyString(key: TileKey): string {
  return `${key.layer} ${key.tx} ${key.ty} ${key.lod}`;
}

/** 从字符串键还原。仅用于调试与持久化读取，热路径不该走这里。 */
export function parseTileKey(text: string): TileKey {
  const parts = text.split(' ');
  if (parts.length !== 4) throw new RangeError(`不是合法的 tile 键：${JSON.stringify(text)}`);
  const [layer, tx, ty, lod] = parts as [string, string, string, string];
  return {
    layer: layer as LayerId,
    tx: Number(tx),
    ty: Number(ty),
    lod: Number(lod) as LodLevel,
  };
}

export function tileKeyEquals(a: TileKey, b: TileKey): boolean {
  return a.layer === b.layer && a.tx === b.tx && a.ty === b.ty && a.lod === b.lod;
}

/* ------------------------------------------------------------------ */
/* 像素坐标 ↔ 块坐标                                                     */
/* ------------------------------------------------------------------ */

/**
 * 某个 LOD 级别下的有效边长（像素）。
 *
 * 块的**像素尺寸恒为 TILE**，变的是它在文档坐标系里覆盖的范围：
 * lod=1 时一块覆盖 512×512 文档像素。混淆这两者会让 LOD 选取整体错位。
 */
export function lodScale(lod: LodLevel): number {
  return 1 << lod;
}

/** 把文档像素坐标换算成指定 LOD 下的块坐标。 */
export function pixelToTile(x: number, y: number, lod: LodLevel = 0): { tx: number; ty: number } {
  const span = TILE * lodScale(lod);
  return { tx: Math.floor(x / span), ty: Math.floor(y / span) };
}

/** 块在文档坐标系里覆盖的矩形（像素）。 */
export function tileBounds(
  tx: number,
  ty: number,
  lod: LodLevel = 0,
): { x: number; y: number; width: number; height: number } {
  const span = TILE * lodScale(lod);
  return { x: tx * span, y: ty * span, width: span, height: span };
}

/** 指定尺寸的文档在某 LOD 下共有多少块。 */
export function tileGridSize(
  documentWidth: number,
  documentHeight: number,
  lod: LodLevel = 0,
): { cols: number; rows: number } {
  const span = TILE * lodScale(lod);
  return {
    cols: Math.max(0, Math.ceil(documentWidth / span)),
    rows: Math.max(0, Math.ceil(documentHeight / span)),
  };
}

/**
 * 遍历一个像素矩形覆盖到的所有块坐标。
 *
 * 用生成器而非返回数组：整幅 10000×10000 文档有 1600 块，
 * 而调用方通常在找到第一个脏块后就会短路。返回数组等于强制它先全部枚举。
 */
export function* tilesInRect(
  x: number,
  y: number,
  width: number,
  height: number,
  lod: LodLevel = 0,
): Generator<{ tx: number; ty: number }> {
  if (width <= 0 || height <= 0) return;
  const span = TILE * lodScale(lod);
  const firstX = Math.floor(x / span);
  const firstY = Math.floor(y / span);
  // 右下边界取闭区间的最后一个像素，避免 x+width 恰好落在块边界时多枚举一列
  const lastX = Math.floor((x + width - 1) / span);
  const lastY = Math.floor((y + height - 1) / span);

  // 跳过负坐标不变（ADR-0012）：这个函数只服务于"文档/画布自身固定网格"或
  // 已经落在非负范围的目标矩形（唯一调用方 place-tool.ts 就是这种），跟
  // "单个图层自己的块坐标可以为负"是两件不同的事——tileKey 放开符号限制
  // 不代表这里也该改。
  for (let ty = firstY; ty <= lastY; ty++) {
    for (let tx = firstX; tx <= lastX; tx++) {
      if (tx >= 0 && ty >= 0) yield { tx, ty };
    }
  }
}
